import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { POSTHOG_HOSTS } from './config';
export const CONSENT_KEY = 'neumind_cookie_consent';
/**
 * Builds the injected PostHog snippet-loader script.
 *
 * `posthog.register(...)` is queued directly after `posthog.init(...)` in
 * the SAME script. The official snippet loader (`window.posthog` acting as
 * a call queue before `array.js` loads async) replays queued calls via
 * `PostHog.prototype._execute_array`, invoked synchronously immediately
 * after `init()` returns inside `init_from_snippet()`
 * (`posthog-js/lib/src/posthog-core.js`) — and `init()` itself only
 * SCHEDULES the automatic $pageview capture via `setTimeout(..., 1)`
 * (`capture_pageview && setTimeout(() => ... this.Vi(), 1)` in the compiled
 * bundle). Because JS is single-threaded, that deferred pageview cannot fire
 * until the synchronous replay — including our queued `register` call — has
 * completed, so `environment` is on the super-properties before the first
 * event, exactly like the dashboard's `loaded`-callback wiring
 * (`frontend/src/utils/posthogOptions.ts`), via a different mechanism
 * (queue-order, not a `loaded` hook) because this provider uses the
 * snippet loader rather than the npm package.
 */
export function buildPostHogInitScript(apiKey, apiHost, optOutByDefault, environment) {
    return `
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init('${apiKey}', {
        api_host: '${apiHost}',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        persistence: 'localStorage',
        opt_out_capturing_by_default: ${optOutByDefault}
      });
      posthog.register({ environment: '${environment}' });
    `;
}
export function PostHogProvider({ apiKey, apiHost = POSTHOG_HOSTS.ui, environment, children, }) {
    useEffect(() => {
        if (!apiKey) {
            console.warn('PostHog: No API key provided');
            return;
        }
        // Check if user has already given consent
        const consent = localStorage.getItem(CONSENT_KEY);
        const optOutByDefault = consent !== 'accepted';
        // Load PostHog script
        const script = document.createElement('script');
        script.innerHTML = buildPostHogInitScript(apiKey, apiHost, optOutByDefault, environment);
        document.head.appendChild(script);
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, [apiKey, apiHost, environment]);
    return _jsx(_Fragment, { children: children });
}
// Typed hook for PostHog analytics
export function usePostHog() {
    const capture = (event, properties) => {
        window.posthog?.capture(event, properties);
    };
    const identify = (distinctId, properties, options) => {
        window.posthog?.identify(distinctId, properties, options);
    };
    const reset = () => {
        window.posthog?.reset();
    };
    const isFeatureEnabled = (key) => {
        return window.posthog?.isFeatureEnabled(key);
    };
    const getFeatureFlag = (key) => {
        return window.posthog?.getFeatureFlag(key);
    };
    const optIn = () => {
        window.posthog?.opt_in_capturing();
    };
    const optOut = () => {
        window.posthog?.opt_out_capturing();
    };
    return {
        capture,
        identify,
        reset,
        isFeatureEnabled,
        getFeatureFlag,
        optIn,
        optOut,
    };
}
